import { hexToRgb } from 'src/utils/common';

export type ButtonColor = 'primary' | 'secondary' | 'tertiary' | 'accent';
export type ButtonVariant = 'contained' | 'outlined' | 'text';
export type ButtonStatus = '&' | '&:hover' | '&:active' | '&:disabled';

type ThemedColorMap = {
  [status in Partial<ButtonStatus>]?: {
    [variant in ButtonVariant]?: {
      [buttonColor in ButtonColor]?: Theme['color'][keyof Theme['color']];
    };
  };
};

const createButtonColorMap = (theme: Theme): ThemedColorMap => ({
  '&': {
    contained: {
      primary: theme.color.primary1,
      secondary: theme.color.surface12,
      tertiary: theme.color.surface5,
      accent: theme.color.accentA1,
    },
    outlined: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: 'transparent',
    },
    text: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: 'transparent',
    },
  },
  '&:hover': {
    contained: {
      primary: theme.color.primary2,
      secondary: theme.color.surface10,
      tertiary: theme.color.surface4,
      accent: theme.color.accentA2,
    },
    outlined: {
      primary: theme.color.surface4,
      secondary: theme.color.surface4,
      tertiary: theme.color.surface4,
    },
    text: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: 'transparent',
    },
  },
  '&:active': {
    contained: {
      primary: theme.color.primary2,
      secondary: theme.color.surface10,
      tertiary: theme.color.surface4,
      accent: theme.color.accentA2,
    },
    outlined: {
      primary: theme.color.surface9,
      secondary: theme.color.surface9,
      tertiary: theme.color.surface9,
    },
    text: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: theme.color.surface4,
    },
  },
  '&:disabled': {
    contained: {
      primary: theme.color.surface4,
      secondary: theme.color.surface4,
      tertiary: theme.color.surface4,
      accent: theme.color.surface4,
    },
    outlined: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: 'transparent',
    },
    text: {
      primary: 'transparent',
      secondary: 'transparent',
      tertiary: 'transparent',
    },
  },
});

const createTextColorMap = (theme: Theme): ThemedColorMap => ({
  '&': {
    contained: {
      primary: theme.color.contentInverse,
      secondary: theme.color.contentBright,
      tertiary: theme.color.content12,
      accent: theme.color.contentBrightFixed,
    },
    outlined: {
      primary: theme.color.content12,
      secondary: theme.color.content12,
      tertiary: theme.color.content12,
    },
    text: {
      primary: theme.color.contentBrightFixed,
      secondary: theme.color.content8,
      tertiary: theme.color.content8,
    },
  },
  '&:hover': {
    text: {
      primary: `rgba(${hexToRgb(theme.color.contentBrightFixed)}, 0.5)`,
      secondary: theme.color.content9,
      tertiary: theme.color.content9,
    },
  },
  '&:active': {
    text: {
      primary: `rgba(${hexToRgb(theme.color.contentBrightFixed)}, 0.5)`,
      secondary: theme.color.content9,
      tertiary: theme.color.content9,
    },
  },
  '&:disabled': {
    contained: {
      primary: theme.color.content6,
      secondary: theme.color.content6,
      tertiary: theme.color.content6,
      accent: theme.color.content6,
    },
    outlined: {
      primary: theme.color.content7,
      secondary: theme.color.content7,
      tertiary: theme.color.content7,
    },
    text: {
      primary: `rgba(${hexToRgb(theme.color.contentBrightFixed)}, 0.3)`,
      secondary: theme.color.content6,
      tertiary: theme.color.content6,
    },
  },
});

const createBorderColorMap = (theme: Theme): ThemedColorMap => ({
  '&': {
    outlined: {
      primary: theme.color.surface9,
      secondary: theme.color.surface9,
      tertiary: theme.color.surface9,
    },
  },
  '&:hover': {
    outlined: {
      primary: theme.color.surface9,
      secondary: theme.color.surface9,
      tertiary: theme.color.surface9,
    },
  },
  '&:active': {
    outlined: {
      primary: theme.color.surface9,
      secondary: theme.color.surface9,
      tertiary: theme.color.surface9,
    },
  },
  '&:disabled': {
    outlined: {
      primary: theme.color.surface9,
      secondary: theme.color.surface9,
      tertiary: theme.color.surface9,
    },
  },
});

export const createColorMap = (
  theme: Theme
): {
  [key: string]: ThemedColorMap;
} => ({
  'background-color': createButtonColorMap(theme),
  color: createTextColorMap(theme),
  'border-color': createBorderColorMap(theme),
});
